'use client';
import React, { useState } from 'react';
import { X, Bell, CheckCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { Product } from '@/data/marketData';

interface AlertSetupPanelProps {
  product: Product;
  lang: 'en' | 'ta';
  onClose: () => void;
}

interface AlertForm {
  condition: 'below' | 'above';
  threshold: number;
}

export default function AlertSetupPanel({ product, lang, onClose }: AlertSetupPanelProps) {
  const [saved, setSaved] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<AlertForm>({
    defaultValues: { condition: 'below', threshold: Math.round(product.currentPrice * 0.8) }
  });

  const condition = watch('condition');
  const threshold = watch('threshold');

  const onSubmit = (data: AlertForm) => {
    // Backend integration point: save alert rule to user's profile
    setSaved(true);
    setTimeout(() => {
      onClose();
      toast.success(`Alert set: ${product.name} ${data.condition} ₹${data.threshold}/kg`);
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card rounded-4xl shadow-float scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary rounded-2xl flex items-center justify-center">
              <Bell size={18} className="text-primary" />
            </div>
            <div>
              <p className="font-bold text-foreground">
                {lang === 'en' ? 'Set Price Alert' : 'விலை எச்சரிக்கை அமை'}
              </p>
              <p className="text-xs text-muted-foreground">
                {lang === 'en' ? product.name : product.nameTa} · {lang === 'en' ? 'Currently' : 'தற்போது'} ₹{product.currentPrice}/{product.unit}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
            <X size={18} />
          </button>
        </div>

        {saved ? (
          <div className="px-6 py-10 text-center">
            <div className="w-16 h-16 bg-green-light rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-primary" />
            </div>
            <p className="font-bold text-foreground text-lg mb-1">
              {lang === 'en' ? 'Alert Saved!' : 'எச்சரிக்கை சேமிக்கப்பட்டது!'}
            </p>
            <p className="text-sm text-muted-foreground">
              {lang === 'en' ? `We'll notify you when ${product.name} goes ${condition} ₹${threshold}/${product.unit}`
                : `${product.nameTa} ₹${threshold}/${product.unit} ${condition === 'below' ? 'கீழே' : 'மேலே'} சென்றால் தெரிவிப்போம்`}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">
                {lang === 'en' ? 'Alert Condition' : 'எச்சரிக்கை நிபந்தனை'}
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'below', labelEn: 'Price drops below', labelTa: 'விலை கீழே சென்றால்', icon: '📉' },
                  { value: 'above', labelEn: 'Price rises above', labelTa: 'விலை மேலே சென்றால்', icon: '📈' },
                ].map(opt => (
                  <label key={`cond-${opt.value}`} className="cursor-pointer">
                    <input type="radio" value={opt.value} {...register('condition')} className="sr-only" />
                    <div className={`p-4 rounded-2xl border text-center transition-all ${condition === opt.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border hover:border-primary'}`}>
                      <span className="text-xl block mb-1">{opt.icon}</span>
                      <span className="text-xs font-semibold">
                        {lang === 'en' ? opt.labelEn : opt.labelTa}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {lang === 'en' ? 'Price Threshold' : 'விலை வரம்பு'}
              </label>
              <p className="text-xs text-muted-foreground mb-3">
                {lang === 'en'
                  ? `Notify me when ${product.name} goes ${condition} this price`
                  : `${product.nameTa} இந்த விலைக்கு ${condition === 'below' ? 'கீழே' : 'மேலே'} சென்றால் தெரிவி`}
              </p>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-primary">₹</span>
                <input
                  type="number"
                  className="input-base pl-9 text-xl font-bold font-tabular"
                  {...register('threshold', {
                    required: 'Please enter a price',
                    min: { value: 1, message: 'Price must be at least ₹1' },
                    max: { value: 9999, message: 'Price too high' },
                  })}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">/{product.unit}</span>
              </div>
              {errors.threshold && (
                <p className="text-xs text-rise-red mt-1.5">{errors.threshold.message}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                {lang === 'en' ? 'Current price:' : 'தற்போதைய விலை:'}{' '}
                <span className="font-semibold text-foreground font-tabular">₹{product.currentPrice}/{product.unit}</span>
              </p>
            </div>

            <button type="submit" className="btn-primary w-full">
              {lang === 'en' ? 'Save Alert' : 'எச்சரிக்கை சேமி'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}